import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, MagnifyingGlass, FileText, CalendarBlank } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function TechnicalReviewQueue() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [form, setForm] = useState({
    client_name: "",
    product_sku: "",
    comanufacturer: "",
    facility: "",
    review_date: new Date().toISOString().split("T")[0]
  });

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const { data } = await api.get("/admin/coman-technical-reviews");
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to load reviews");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = reviews.filter(r => 
    r.client_name?.toLowerCase().includes(search.toLowerCase()) || 
    r.product_sku?.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.product_sku || !form.comanufacturer || !form.facility) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/coman-technical-reviews", form);
      toast.success("Review created");
      navigate(`/admin/technical-reviews/${data.id}`);
    } catch (err) {
      toast.error("Failed to create review");
      console.error(err);
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-stone-900">Technical Reviews</h1>
            <p className="text-sm text-stone-500 mt-1">Commercialization readiness workspace.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 text-white shrink-0 whitespace-nowrap">
            <Plus className="mr-2" size={16} weight="bold" /> New Review
          </Button>
        </div>

        <div className="mb-6 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-stone-400">
            <MagnifyingGlass size={16} />
          </div>
          <Input 
            type="text" 
            placeholder="Search by client or SKU..."
            className="pl-10 max-w-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="text-sm text-stone-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center border-stone-200">
            <div className="mx-auto w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center mb-4 text-stone-400">
              <FileText size={24} />
            </div>
            <h3 className="text-lg font-semibold text-stone-900">No reviews found</h3>
            <p className="text-sm text-stone-500 mt-1">Get started by creating a new technical review.</p>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-stone-200">
                <thead className="bg-stone-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-stone-500 uppercase tracking-wider whitespace-nowrap">Client / Product</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-stone-500 uppercase tracking-wider whitespace-nowrap">Facility</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-stone-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-stone-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th scope="col" className="relative px-6 py-3"><span className="sr-only">Open</span></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-stone-200">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-stone-900">{r.client_name}</div>
                        <div className="text-xs text-stone-500 font-mono">{r.product_sku}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-stone-900">{r.comanufacturer}</div>
                        <div className="text-xs text-stone-500">{r.facility}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-stone-600">
                          <CalendarBlank size={14} className="mr-1.5 text-stone-400" />
                          {new Date(r.review_date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wider
                          ${r.overall_status === 'Approved' ? 'bg-emerald-100 text-emerald-800' : 
                            r.overall_status === 'Needs Action' ? 'bg-amber-100 text-amber-800' : 
                            'bg-stone-100 text-stone-600'}`}>
                          {r.overall_status || 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/admin/technical-reviews/${r.id}`} className="text-emerald-700 hover:text-emerald-900">
                          Open &rarr;
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>New Technical Review</DialogTitle>
                <DialogDescription>Setup a new workspace for commercialization review.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="client_name">Client Name</Label>
                  <Input id="client_name" required value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="product_sku">Product SKU / Code</Label>
                  <Input id="product_sku" required value={form.product_sku} onChange={e => setForm({...form, product_sku: e.target.value})} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="comanufacturer">Co-Manufacturer</Label>
                  <Input id="comanufacturer" required value={form.comanufacturer} onChange={e => setForm({...form, comanufacturer: e.target.value})} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="facility">Facility Location</Label>
                  <Input id="facility" required value={form.facility} onChange={e => setForm({...form, facility: e.target.value})} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="review_date">Review Date</Label>
                  <Input id="review_date" type="date" required value={form.review_date} onChange={e => setForm({...form, review_date: e.target.value})} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={creating} className="bg-emerald-800 text-white hover:bg-emerald-900">
                  {creating ? "Creating..." : "Create Workspace"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
